import { useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { LayoutGrid, Image, MessageCircle, Zap, Loader, WifiOff } from 'lucide-react';
import { useParticipantPolling } from './useParticipantPolling';
import { SessionTab } from './tabs/SessionTab';
import { AssetsTab } from './tabs/AssetsTab';
import { ChatTab } from './tabs/ChatTab';
import { ActivityTab } from './tabs/ActivityTab';

type Tab = 'session' | 'assets' | 'chat' | 'activity';

const TABS: { id: Tab; label: string; Icon: React.FC<any> }[] = [
  { id: 'session', label: 'Session', Icon: LayoutGrid },
  { id: 'assets', label: 'Assets', Icon: Image },
  { id: 'chat', label: 'Chat', Icon: MessageCircle },
  { id: 'activity', label: 'Activity', Icon: Zap },
];

export default function ParticipantView() {
  useParams<{ token: string }>();
  const location = useLocation();
  const sessionId = (location.state as any)?.sessionId;
  const [activeTab, setActiveTab] = useState<Tab>('session');

  const { state, error, loading, refresh } = useParticipantPolling(sessionId ?? null);

  if (!sessionId) {
    return (
      <div className="fixed inset-0 bg-navy flex items-center justify-center p-6">
        <p className="text-white/50 text-sm">Invalid session — please scan your QR code again.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-navy flex items-center justify-center">
        <Loader size={28} className="text-primary animate-spin" />
      </div>
    );
  }

  if (error && !state) {
    return (
      <div className="fixed inset-0 bg-navy flex flex-col items-center justify-center p-6 gap-4">
        <WifiOff size={32} className="text-white/30" />
        <p className="text-white/50 text-sm text-center">Connection lost — {error}</p>
        <button onClick={refresh} className="text-primary text-sm font-medium">Retry</button>
      </div>
    );
  }

  if (!state) return null;

  const hasActiveWidget = !!state.activeWidget;

  return (
    <div className="fixed inset-0 bg-navy overflow-hidden flex flex-col">
      <div className="h-full max-w-lg mx-auto w-full flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 px-4 pt-safe pb-3 border-b border-white/10" style={{ paddingTop: 'max(env(safe-area-inset-top), 1rem)' }}>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-white font-semibold text-base leading-tight truncate max-w-56">{state.sessionName}</h1>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                <span className="text-white/40 text-xs">Live</span>
              </div>
            </div>
            {error && (
              <span className="text-xs text-yellow-400/70 bg-yellow-400/10 px-2 py-1 rounded-full">Offline</span>
            )}
          </div>
        </div>

        {/* Tab content */}
        <div className="flex-1 flex flex-col overflow-hidden min-h-0">
          {activeTab === 'session' && <SessionTab state={state} sessionId={sessionId} />}
          {activeTab === 'assets' && <AssetsTab assets={state.publishedAssets} />}
          {activeTab === 'chat' && <ChatTab sessionId={sessionId} state={state} />}
          {activeTab === 'activity' && (
            <ActivityTab
              sessionId={sessionId}
              widget={state.activeWidget}
              onSubmit={refresh}
            />
          )}
        </div>

        {/* Bottom tab bar */}
        <div className="flex-shrink-0 bg-navy border-t border-white/10" style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 0.25rem)' }}>
          <div className="flex">
            {TABS.map(({ id, label, Icon }) => {
              const isActive = activeTab === id;
              const showBadge = id === 'activity' && hasActiveWidget;
              return (
                <button
                  key={id}
                  onClick={() => setActiveTab(id)}
                  className={`flex-1 flex flex-col items-center gap-1 py-3 transition-colors relative ${
                    isActive ? 'text-primary' : 'text-white/40'
                  }`}
                >
                  <div className="relative">
                    <Icon size={20} />
                    {showBadge && (
                      <span className="absolute -top-1 -right-1 w-2 h-2 bg-primary rounded-full" />
                    )}
                  </div>
                  <span className="text-xs font-medium">{label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
